import { useState, useMemo } from "react";

import { BsMetaUtil, useSummarizedUserAssets, useTokenListContext } from "@bridgesplit/abf-react";
import {
    useAppPalette,
    useTabbableList,
    Column,
    DIALOG_PADDING,
    SearchInput,
    VerticalScroll,
    repeatElement,
    SkeletonRounded,
    Row,
    Text
} from "@bridgesplit/ui";
import { formatUsd, formatTokenAmount, textContains } from "@bridgesplit/utils";
import { TokenImage } from "app/components/common";

import { CreateRewardForm, EditRewardsForm } from "./types";

type CollateralSelectProps<T extends string, F extends CreateRewardForm | EditRewardsForm> = {
    form: F;
    setForm: (form: F) => void;
    setStep: (step: T) => void;
    nextStep: T;
};

export function CollateralSelect<T extends string, F extends CreateRewardForm | EditRewardsForm>({
    form,
    setForm,
    setStep,
    nextStep
}: CollateralSelectProps<T, F>) {
    const [search, setSearch] = useState("");
    const { hoverBackground } = useAppPalette();

    // Get all collateral tokens from token list
    // Managers set terms on tokens they don't hold, so this skips the wallet-only filter.
    const { allCollateralTokens: collateralTokens } = useTokenListContext();

    // Get user balances
    const { balanceMap: userBalanceMap, isLoading } = useSummarizedUserAssets({
        includeLpPositions: false
    });

    // Sort tokens by user balance
    const tokens = useMemo(() => {
        if (!collateralTokens || !userBalanceMap) return collateralTokens;

        return [...collateralTokens].sort((a, b) => {
            const balanceA = userBalanceMap.get(a.mint)?.available || 0;
            const balanceB = userBalanceMap.get(b.mint)?.available || 0;
            return balanceB - balanceA;
        });
    }, [collateralTokens, userBalanceMap]);

    const { activeIndex, itemRefs, containerRef, setActiveIndex } = useTabbableList(() => {
        const token = filteredTokens?.[activeIndex];
        if (!token) return;
        setForm({
            ...form,
            rewardToken: token
        });
        setStep(nextStep);
    }, tokens?.length ?? 0);

    const filteredTokens = tokens?.filter((token) =>
        search
            ? token.mint === search || textContains([BsMetaUtil.getName(token), BsMetaUtil.getSymbol(token)], search)
            : true
    );

    return (
        <Column spacing={2} sx={{ mx: -DIALOG_PADDING }}>
            <SearchInput
                forceFocus
                sx={{ px: DIALOG_PADDING }}
                placeholder="Search by symbol or address"
                onChange={(e) => {
                    setSearch(e.target.value);
                    setActiveIndex(0);
                }}
                value={search}
            />

            <VerticalScroll ref={containerRef} maxHeight={500}>
                {!filteredTokens || isLoading
                    ? repeatElement(<SkeletonRounded sx={{ mx: DIALOG_PADDING, my: 0.5 }} height={50} />, 5)
                    : filteredTokens?.map((token, i) => (
                          <Row
                              ref={(el) => (el ? (itemRefs.current[i] = el) : null)}
                              key={token.mint}
                              sx={{
                                  px: DIALOG_PADDING,
                                  py: 1,
                                  cursor: "pointer",
                                  background: i === activeIndex ? hoverBackground : undefined,
                                  "&:hover": { background: hoverBackground }
                              }}
                              onClick={() => {
                                  setForm({
                                      ...form,
                                      rewardToken: token
                                  });
                                  setStep(nextStep);
                              }}
                              spaceBetween
                          >
                              <Column>
                                  <Row spacing={1.5}>
                                      <TokenImage metadata={token} size="md" />
                                      <Column spacing={0.5}>
                                          <Row spacing={1}>
                                              <Text>{BsMetaUtil.getSymbolUnique(token)}</Text>
                                          </Row>
                                          <Text>{BsMetaUtil.getName(token)}</Text>
                                      </Column>
                                  </Row>
                              </Column>
                              {userBalanceMap?.get(token.mint) && (
                                  <Column alignItems="flex-end">
                                      <Text>
                                          {formatTokenAmount(userBalanceMap?.get(token.mint)?.available, token)}
                                      </Text>
                                      <Text color="caption" variant="body2">
                                          {formatUsd(userBalanceMap?.get(token.mint)?.availableUsd)}
                                      </Text>
                                  </Column>
                              )}
                          </Row>
                      ))}
            </VerticalScroll>
        </Column>
    );
}
